import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface RosterStudent {
  id: string;
  student_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  class?: string | null;
  schoolFeeInvoice?: any | null;
}

interface Props {
  students: RosterStudent[] | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}

type Row = {
  studentId: string;
  studentNumber: string;
  name: string;
  className: string;
  invoiceId: string | null;
  billed: number;
  originalDiscount: number;
  finalInput: string; // editable
};

const fmt = (n: number) => new Intl.NumberFormat().format(Math.round(n));

export function BulkEditAdjustmentDialog({ students, open, onOpenChange, onSaved }: Props) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    if (!open || !students) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const ids = students.map(s => s.id);
        const { data: invs, error } = await supabase
          .from("invoices")
          .select("id, student_id, amount, discount_amount, kind, control_number, created_at")
          .in("student_id", ids)
          .eq("kind", "school_fee")
          .order("created_at", { ascending: false });
        if (error) throw error;
        const byStudent: Record<string, any> = {};
        for (const inv of invs ?? []) {
          if (!byStudent[inv.student_id]) {
            byStudent[inv.student_id] = inv;
          } else if (inv.control_number && !byStudent[inv.student_id].control_number) {
            byStudent[inv.student_id] = inv;
          }
        }
        if (cancelled) return;
        const built: Row[] = students.map(s => {
          const inv = byStudent[s.id];
          const billed = Number(inv?.amount ?? 0);
          const discount = Number(inv?.discount_amount ?? 0);
          const final = Math.max(billed - discount, 0);
          return {
            studentId: s.id,
            studentNumber: s.student_number ?? "—",
            name: [s.first_name, s.last_name].filter(Boolean).join(" ") || "—",
            className: s.class ?? "—",
            invoiceId: inv?.id ?? null,
            billed,
            originalDiscount: discount,
            finalInput: inv ? String(final) : "",
          };
        });
        setRows(built);
      } catch (e: any) {
        toast({ title: "Failed to load invoices", description: e.message, variant: "destructive" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, students]);

  const stats = useMemo(() => {
    let ready = 0, skipped = 0, invalid = 0, changed = 0, totalDiscount = 0;
    for (const r of rows) {
      if (!r.invoiceId) { skipped++; continue; }
      const finalNum = r.finalInput === "" ? NaN : Number(r.finalInput);
      if (Number.isNaN(finalNum)) { skipped++; continue; }
      if (finalNum < 0 || finalNum > r.billed) { invalid++; continue; }
      ready++;
      const newDiscount = r.billed - finalNum;
      totalDiscount += newDiscount;
      if (Math.abs(newDiscount - r.originalDiscount) > 0.001) changed++;
    }
    return { ready, skipped, invalid, changed, totalDiscount };
  }, [rows]);

  const updateFinal = (id: string, v: string) => {
    setRows(prev => prev.map(r => r.studentId === id ? { ...r, finalInput: v } : r));
  };

  const apply = async () => {
    const targets = rows.filter(r => {
      if (!r.invoiceId) return false;
      const f = Number(r.finalInput);
      if (Number.isNaN(f) || f < 0 || f > r.billed) return false;
      const newDiscount = r.billed - f;
      return Math.abs(newDiscount - r.originalDiscount) > 0.001;
    });
    if (!targets.length) {
      toast({ title: "No changes to apply" });
      return;
    }
    setSaving(true);
    setProgress({ done: 0, total: targets.length });
    let ok = 0, fail = 0;
    // simple concurrency = 5
    const queue = [...targets];
    const workers = Array.from({ length: Math.min(5, queue.length) }, async () => {
      while (queue.length) {
        const r = queue.shift()!;
        const newDiscount = r.billed - Number(r.finalInput);
        const { error } = await supabase.from("invoices")
          .update({ discount_amount: newDiscount })
          .eq("id", r.invoiceId!);
        if (error) fail++; else ok++;
        setProgress(p => ({ ...p, done: p.done + 1 }));
      }
    });
    await Promise.all(workers);
    setSaving(false);
    toast({
      title: "Bulk adjustment complete",
      description: `Updated ${ok}${fail ? ` · Failed ${fail}` : ""}`,
      variant: fail ? "destructive" : "default",
    });
    onSaved();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Bulk Edit Adjustment</DialogTitle>
          <DialogDescription>
            Set the Final Amount each student should pay after scholarship or exemption. The Discount Amount is calculated automatically as Billed − Final.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-40 text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading invoices…
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-auto border rounded-md">
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Student #</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead className="text-right">Billed</TableHead>
                  <TableHead className="text-right w-40">Final</TableHead>
                  <TableHead className="text-right">Discount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => {
                  const finalNum = r.finalInput === "" ? NaN : Number(r.finalInput);
                  const invalid = !r.invoiceId
                    ? false
                    : Number.isNaN(finalNum) || finalNum < 0 || finalNum > r.billed;
                  const discount = !r.invoiceId || Number.isNaN(finalNum) ? null : r.billed - finalNum;
                  return (
                    <TableRow key={r.studentId}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell className="font-mono text-xs">{r.studentNumber}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.className}</TableCell>
                      <TableCell className="text-right">
                        {r.invoiceId ? fmt(r.billed) : <span className="text-xs text-muted-foreground">No invoice</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.invoiceId ? (
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            max={r.billed}
                            value={r.finalInput}
                            onChange={(e) => updateFinal(r.studentId, e.target.value)}
                            className={`h-8 text-right ${invalid ? "border-destructive" : ""}`}
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className={`text-right ${discount && discount < 0 ? "text-destructive" : ""}`}>
                        {discount == null ? "—" : fmt(discount)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
          <span>{stats.ready} ready</span>
          <span>{stats.changed} changed</span>
          {stats.skipped > 0 && <span>{stats.skipped} skipped (no invoice)</span>}
          {stats.invalid > 0 && <span className="text-destructive">{stats.invalid} invalid</span>}
          <span className="ml-auto">Total discount: <span className="font-medium text-foreground">TZS {fmt(stats.totalDiscount)}</span></span>
        </div>

        {saving && (
          <div className="text-xs text-muted-foreground">Saving {progress.done}/{progress.total}…</div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={apply} disabled={saving || loading || stats.changed === 0 || stats.invalid > 0}>
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying…</> : `Apply (${stats.changed})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
